import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import clsx from 'clsx';
import {diApiNa} from '@di-shared/api/di';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ROUTE_PATHS} from '@di-shared/routes';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface HomeTireNaProps {
  vin: string;
}

enum TireStatus {
  NOT_REGISTERED,
  WARNING,
  GOOD,
}

function HomeTireNa({vin}: HomeTireNaProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();

  return (
    <ErrorBoundary
      fallback={
        <section className="section section_car_diagnosis">
          <ul className="car_diagnosis_list">
            <li className="car_diagnosis_item">
              <a
                className="car_diagnosis_link"
                onClick={() => navigate(ROUTE_PATHS.DIAG_TIRE.getFullPath(vin))}
              >
                <div className="section_container">
                  <strong className="car_diagnosis_title">{t('ctn_tire_title_eu')}</strong>
                  <span className="ic ic_tire_good" />
                </div>
              </a>
            </li>
          </ul>
        </section>
      }
    >
      <Suspense
        fallback={
          <section className="section section_car_diagnosis">
            <ul className="car_diagnosis_list">
              <li className="car_diagnosis_item">
                <div
                  className="car_diagnosis_link skeleton"
                  style={{height: 90}}
                />
              </li>
            </ul>
          </section>
        }
      >
        <HomeTireNaInner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function HomeTireNaInner({vin}: HomeTireNaProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {data: tireBoundData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiNa.tire.getTireBound), vin],
    queryFn: () => diApiNa.tire.getTireBound({vin}),
    select: ({data}) => data,
  });

  function calculateTireStatus(): TireStatus {
    const {flBound, frBound, rlBound, rrBound} = tireBoundData;
    const isTireRegistered = [flBound, frBound, rlBound, rrBound].every((bound) => !!bound);
    if (!isTireRegistered) {
      return TireStatus.NOT_REGISTERED;
    }
    const hasWarning = ![flBound, frBound, rlBound, rrBound].every((bound) => bound === 'inbound' || bound === 'higher');
    return hasWarning ? TireStatus.WARNING : TireStatus.GOOD;
  }

  const tireStatus = calculateTireStatus();

  return (
    <section className="section section_car_diagnosis">
      <ul className="car_diagnosis_list">
        <li className="car_diagnosis_item">
          <a
            className="car_diagnosis_link"
            onClick={() => navigate(ROUTE_PATHS.DIAG_TIRE.getFullPath(vin))}
          >
            <div className="section_container">
              <strong className="car_diagnosis_title">{t('ctn_tire_title_eu')}</strong>
              {tireStatus === TireStatus.GOOD ? (
                <div className="badge_box">
                  <span className="badge badge_basic_container badge_20px">{t('ctn_tire_status_good')}</span>
                </div>
              ) : tireStatus === TireStatus.WARNING ? (
                <div className="badge_box">
                  <span className="badge badge_container_red badge_20px">{t('ctn_tire_status_Needservice')}</span>
                </div>
              ) : (
                <></>
              )}
              <span
                className={clsx(
                  'ic',
                  tireStatus === TireStatus.GOOD || tireStatus === TireStatus.NOT_REGISTERED
                    ? 'ic_tire_good'
                    : 'ic_tire_warning'
                )}
              />
            </div>
          </a>
        </li>
      </ul>
    </section>
  );
}

export default HomeTireNa;
